import { useEffect, useRef, useState } from 'react';

// Virtuelles Bier — der ganze Bildschirm IST das Bier.
//  - Surface bleibt horizontal in Welt-Koordinaten (Wasserwage). Phone rotiert,
//    Bier-Oberfläche neigt sich gegen die Phone-Achse.
//  - Bei zu starker Neigung läuft das Bier oben aus (Pegel sinkt).
//  - 90° gekippt = leer.
//  - Tap = Schluck.

export function BeerModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" style={{ background: '#1a0f04' }}>
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 pointer-events-none">
        <div className="text-xs uppercase tracking-widest font-bold pointer-events-auto" style={{ color: 'rgba(255,255,255,0.75)' }}>🍺 Virtuelles Bier</div>
        <button onClick={onClose} className="text-2xl leading-none w-10 h-10 flex items-center justify-center pointer-events-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>×</button>
      </div>
      <div className="absolute inset-0">
        <BeerCanvas />
      </div>
    </div>
  );
}

function BeerCanvas() {
  const canvasRef = useRef(null);
  const [permState, setPermState] = useState(() => {
    if (typeof DeviceMotionEvent === 'undefined') return 'unsupported';
    if (typeof DeviceMotionEvent.requestPermission !== 'function') return 'auto';
    return 'pending';
  });
  const [refillBtn, setRefillBtn] = useState(false);

  const stateRef = useRef({
    gamma: 0,           // raw device tilt in degrees (-90..90)
    smoothedAngle: 0,   // smoothed radians
    angleVel: 0,
    surfaceWobble: 0,
    foamPhase: 0,
    volume: 0.7,        // 0..1
    touchAngle: 0,
    lastTouch: null,
  });

  const requestPerm = async () => {
    try {
      const res = await DeviceMotionEvent.requestPermission();
      setPermState(res === 'granted' ? 'granted' : 'denied');
    } catch { setPermState('denied'); }
  };

  // Device orientation
  useEffect(() => {
    if (permState !== 'auto' && permState !== 'granted') return;
    const onOrient = e => {
      const g = Math.max(-90, Math.min(90, e.gamma ?? 0));
      stateRef.current.gamma = g;
    };
    window.addEventListener('deviceorientation', onOrient);
    return () => window.removeEventListener('deviceorientation', onOrient);
  }, [permState]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const cssW = canvas.clientWidth, cssH = canvas.clientHeight;
      canvas.width  = cssW * dpr;
      canvas.height = cssH * dpr;
    };
    fit();
    window.addEventListener('resize', fit);
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let lastTs = 0;

    function tick(ts) {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.05, (ts - lastTs) / 1000 || 0.016);
      lastTs = ts;
      const s = stateRef.current;

      // Target angle: orientation OR touch drag
      const orientAngle = s.gamma * (Math.PI / 180);  // full mapping; 90° tilt → π/2 rad
      const target = (permState === 'granted' || permState === 'auto') ? orientAngle : s.touchAngle;

      // Spring damper for smoothness (no overshoot)
      const k = 14, c = 6;
      const accel = k * (target - s.smoothedAngle) - c * s.angleVel;
      s.angleVel += accel * dt;
      s.smoothedAngle += s.angleVel * dt;

      s.surfaceWobble = s.angleVel * 0.5;
      s.foamPhase += dt * 2.4;

      // Touch decay
      if (!s.lastTouch) s.touchAngle += (0 - s.touchAngle) * 0.05;

      // Spill physics — based on canvas dimensions
      const w = canvas.width, h = canvas.height;
      const θ = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, s.smoothedAngle));
      const sl = Math.tan(θ);
      const surfaceMidY = h * (1 - s.volume);
      // Surface y at left/right edges (in canvas px)
      const ySurfL = surfaceMidY + (w / 2) * sl;
      const ySurfR = surfaceMidY - (w / 2) * sl;
      const minSurfY = Math.min(ySurfL, ySurfR);
      // Spill if surface above top edge anywhere
      if (minSurfY < 0 && s.volume > 0) {
        const overflowFrac = Math.min(1, -minSurfY / h);
        const rate = 0.6 * overflowFrac + 0.15 * Math.pow(overflowFrac, 0.3);
        s.volume = Math.max(0, s.volume - rate * dt);
      }
      // Almost-vertical → drain remaining quickly
      if (Math.abs(θ) > 1.3 && s.volume > 0) {
        s.volume = Math.max(0, s.volume - 0.4 * dt);
      }

      draw(ctx, w, h, s, θ);
    }
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', fit); };
  }, [permState]);

  useEffect(() => {
    const id = setInterval(() => setRefillBtn(stateRef.current.volume < 0.03), 250);
    return () => clearInterval(id);
  }, []);

  function draw(ctx, w, h, s, θ) {
    // Background: dark amber where there's no beer
    ctx.clearRect(0, 0, w, h);
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#1a0f04');
    bg.addColorStop(1, '#0a0500');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    if (s.volume <= 0.001) {
      // Empty
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.font = `bold ${Math.round(w * 0.06)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('leer', w / 2, h * 0.5);
      ctx.font = `${Math.round(w * 0.035)}px sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillText('zapfen 👉', w / 2, h * 0.5 + Math.round(w * 0.07));
      return;
    }

    const cx = w / 2;
    const sl = Math.tan(θ);
    const surfaceMidY = h * (1 - s.volume);

    // Wobble & wave parameters — dezent, kein Wirbelwind
    const wobble = (Math.min(4, Math.abs(s.surfaceWobble) * 20 + 0.6)) * dprFactor();

    // 1) Fill beer (everything below the tilted surface)
    const lg = ctx.createLinearGradient(0, surfaceMidY, 0, h);
    lg.addColorStop(0, '#fcd34d');
    lg.addColorStop(0.18, '#f59e0b');
    lg.addColorStop(0.85, '#b45309');
    lg.addColorStop(1, '#78350f');
    ctx.fillStyle = lg;
    ctx.beginPath();
    const segs = 40;
    // Top edge of beer = surface (left → right)
    for (let i = 0; i <= segs; i++) {
      const x = (w * i) / segs;
      const t = i / segs;
      const baseY = surfaceMidY - (x - cx) * sl;
      const y = baseY + Math.sin(s.foamPhase + t * 5.5) * wobble
                      + Math.sin(s.foamPhase * 1.6 + t * 11.2) * wobble * 0.4;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    // Right edge down, bottom, left edge up
    ctx.lineTo(w + 10, h + 10);
    ctx.lineTo(-10, h + 10);
    ctx.closePath();
    ctx.fill();

    // 2) Foam layer above surface — dezenter Schaumstreifen
    const foamH = Math.max(10, w * 0.025) * dprFactor();
    ctx.beginPath();
    // Top of foam
    for (let i = 0; i <= segs; i++) {
      const x = (w * i) / segs;
      const t = i / segs;
      const baseY = surfaceMidY - (x - cx) * sl;
      const ly = baseY + Math.sin(s.foamPhase + t * 5.5) * wobble;
      const fy = ly - foamH - Math.sin(s.foamPhase * 1.3 + t * 8.5) * foamH * 0.18;
      if (i === 0) ctx.moveTo(x, fy);
      else ctx.lineTo(x, fy);
    }
    // Bottom of foam (= surface)
    for (let i = segs; i >= 0; i--) {
      const x = (w * i) / segs;
      const t = i / segs;
      const baseY = surfaceMidY - (x - cx) * sl;
      const ly = baseY + Math.sin(s.foamPhase + t * 5.5) * wobble;
      ctx.lineTo(x, ly + 1);
    }
    ctx.closePath();
    const fg = ctx.createLinearGradient(0, surfaceMidY - foamH, 0, surfaceMidY + 2);
    fg.addColorStop(0, '#fffbeb');
    fg.addColorStop(0.7, '#fde68a');
    fg.addColorStop(1, '#fcd34d');
    ctx.fillStyle = fg;
    ctx.fill();

    // Foam bubbles dotted on top
    for (let i = 0; i < 26; i++) {
      const seed = i * 17.31;
      const xR = (seed * 91) % w;
      const t = (xR / w);
      const baseY = surfaceMidY - (xR - cx) * sl;
      const ly = baseY + Math.sin(s.foamPhase + t * 5.5) * wobble;
      const by = ly - foamH * 0.4 + ((seed * 13) % foamH) * 0.4;
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.beginPath();
      ctx.arc(xR, by, 1 + ((seed * 3) % 3) * dprFactor() * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Rising bubbles in beer (in screen coords below the surface)
    for (let i = 0; i < 22; i++) {
      const seed = i * 13.7;
      const t = (s.foamPhase * 0.35 + seed * 0.21) % 1;
      const bx = ((seed * 173) % w);
      const by = h - t * h * 1.1;
      const r = (1 + ((seed * 3) % 3)) * dprFactor() * 0.6;
      const baseY = surfaceMidY - (bx - cx) * sl;
      if (by > baseY) {
        ctx.fillStyle = `rgba(255,255,255,${0.4 - t * 0.3})`;
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  const onTap = () => {
    const s = stateRef.current;
    if (s.volume <= 0.02) return;
    s.volume = Math.max(0, s.volume - 0.06);
    s.angleVel -= 0.8;
  };

  const onTouchStart = e => {
    const t = e.touches?.[0] || e;
    stateRef.current.lastTouch = { x: t.clientX };
  };
  const onTouchMove = e => {
    const t = e.touches?.[0] || e;
    const s = stateRef.current;
    if (!s.lastTouch) return;
    const dx = t.clientX - s.lastTouch.x;
    const w = canvasRef.current.clientWidth;
    s.touchAngle += (dx / w) * 0.8;
    s.touchAngle = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, s.touchAngle));
    s.lastTouch = { x: t.clientX };
  };
  const onTouchEnd = () => { stateRef.current.lastTouch = null; };
  const refill = () => { stateRef.current.volume = 0.85; stateRef.current.angleVel = 0.6; };

  return (
    <div className="w-full h-full relative" style={{ touchAction: 'none' }}>
      <canvas ref={canvasRef} className="w-full h-full block"
        onClick={onTap}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onMouseDown={onTouchStart} onMouseMove={e => e.buttons && onTouchMove(e)} onMouseUp={onTouchEnd}
      />
      {permState === 'pending' && (
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center max-w-sm pointer-events-auto rounded-2xl p-6"
            style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="text-5xl mb-3">📱</div>
            <div className="font-bold mb-2 text-white">Bewegungssensoren freigeben?</div>
            <div className="text-sm mb-4" style={{ color: '#a1a1aa' }}>
              Damit das Bier wie eine Wasserwaage waagerecht bleibt, brauchen wir Zugriff auf die Sensoren.
            </div>
            <button onClick={requestPerm}
              className="rounded-xl px-5 py-3 font-bold active:scale-95"
              style={{ background: '#f5c518', color: '#000' }}>
              Sensoren aktivieren
            </button>
            <div className="text-xs mt-3" style={{ color: '#71717a' }}>
              Sonst: Wischen kippt den Pegel.
            </div>
          </div>
        </div>
      )}
      {refillBtn && (
        <button onClick={refill}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-xl px-5 py-3 font-bold active:scale-95"
          style={{ background: '#22c55e', color: '#000' }}>
          🍺 Nachzapfen
        </button>
      )}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-center px-4"
        style={{ color: 'rgba(255,255,255,0.55)', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
        Tippen = Schluck · Stark kippen = trinken
      </div>
    </div>
  );
}

function dprFactor() { return Math.min(window.devicePixelRatio || 1, 2); }
